var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Observable } from "rxjs";
import { getEnv } from "@ledgerhq/live-env";
import { ledgerService } from "@ledgerhq/hw-app-eth";
import { isNFTActive } from "@ledgerhq/coin-framework/nft/support";
import { buildOptimisticOperation } from "./buildOptimisticOperation";
import { prepareForSignOperation } from "./prepareTransaction";
import { getSerializedTransaction } from "./transaction";
/**
 * Transforms the ECDSA signature paremeter v hexadecimal string received
 * from the nano into an EIP155 compatible number.
 *
 * Reminder EIP155 transforms v this way:
 * v = chainId * 2 + 35
 * (+ parity 1 or 0)
 */
export const applyEIP155 = (vAsHex, chainId) => {
    const v = parseInt(vAsHex, 16);
    if (v === 0 || v === 1) {
        // if v is 0 or 1, it's already representing parity
        return chainId * 2 + 35 + v;
    }
    else if (v === 27 || v === 28) {
        const parity = v - 27; // transforming v into 0 or 1 to become the parity
        return chainId * 2 + 35 + parity;
    }
    // When chainId is lower than 109, hw-app-eth *can* return a v with EIP155 already applied
    // e.g. bsc's chainId is 56 -> v then equals to 147/148
    //      optimism's chainId is 10 -> v equals to 55/56
    //      ethereum's chainId is 1 -> v equals to 0/1
    //      goerli's chainId is 5 -> v equals to 0/1
    return v;
};
/**
 * Sign Transaction with Ledger hardware
 */
export const buildSignOperation = (signerContext) => ({ account, deviceId, transaction, }) => new Observable(o => {
    function main() {
        return __awaiter(this, void 0, void 0, function* () {
            const preparedTransaction = yield prepareForSignOperation(account, transaction);
            const serializedTxHexString = getSerializedTransaction(preparedTransaction).slice(2); // Remove 0x prefix
            // Configure type of resolutions necessary for the clear signing
            const resolutionConfig = {
                externalPlugins: true,
                erc20: true,
                nft: isNFTActive(account.currency),
                domains: transaction.recipientDomain ? [transaction.recipientDomain] : [],
            };
            const loadConfig = {
                cryptoassetsBaseURL: getEnv("DYNAMIC_CAL_BASE_URL"),
                nftExplorerBaseURL: getEnv("NFT_ETH_METADATA_SERVICE") + "/v1/ethereum",
            };
            // Look for resolutions for external plugins and ERC20
            const resolution = yield ledgerService.resolveTransaction(serializedTxHexString, loadConfig, resolutionConfig);
            o.next({
                type: "device-signature-requested",
            });
            const sig = (yield signerContext(deviceId, signer => {
                signer.setLoadConfig(loadConfig);
                // Request signature on the nano
                return signer.signTransaction(account.freshAddressPath, serializedTxHexString, resolution);
            }));
            o.next({ type: "device-signature-granted" }); // Signature is done
            const { chainId = 0 } = account.currency.ethereumLikeInfo || /* istanbul ignore next */ {};
            // Create a new serialized tx with the signature now
            const signature = yield getSerializedTransaction(preparedTransaction, {
                r: "0x" + sig.r,
                s: "0x" + sig.s,
                v: applyEIP155(typeof sig.v === "number" ? sig.v.toString(16) : sig.v, chainId),
            });
            const operation = buildOptimisticOperation(account, Object.assign(Object.assign({}, transaction), { nonce: preparedTransaction.nonce }));
            o.next({
                type: "signed",
                signedOperation: {
                    operation,
                    signature,
                    expirationDate: null,
                },
            });
        });
    }
    main().then(() => o.complete(), 
    /* istanbul ignore next: don't test throwing an error */
    e => o.error(e));
});
//# sourceMappingURL=signOperation.js.map